import { DateTime } from "luxon";
import tools from "../../../helpers/tools";
import { getOrientationTypeLabel } from "../../../helpers/orientationTypes";
import roleConfig from "../../common/layout/roleConfig";

// Synthèse d'identité de la fiche allocataire — fonctions pures (testables).

const formatDate = (value) => (value ? tools.formatDate(value) : "");

export function formatBirthdate(birthdate) {
  if (!birthdate) return "";
  const dt = DateTime.fromISO(birthdate);
  if (!dt.isValid) return "";
  const age = Math.floor(Math.abs(DateTime.local().diff(dt, "years").years));
  return `${dt.toFormat("dd/MM/yyyy")} (${age} an${age > 1 ? "s" : ""})`;
}

export function formatAddress(recipient) {
  const line1 = [recipient.address, recipient.addressAdd].filter(Boolean).join(", ");
  const loc = recipient.location || {};
  const line2 = [loc.postalCode, loc.name].filter(Boolean).join(" ");
  return [line1, line2].filter(Boolean).join(" — ");
}

export function buildRecipientFacts(recipient, { constants = {} } = {}) {
  const maritalStatus = tools.findIn(constants.MARITAL_STATUS || [], "id", recipient.maritalStatus).name;
  return [
    { id: "nir", label: "NIR", value: recipient.nir, copyValue: recipient.nir, mono: true },
    { id: "CAFId", label: "N° d'allocataire", value: recipient.CAFId, copyValue: recipient.CAFId, mono: true },
    { id: "birthdate", label: "Naissance", value: formatBirthdate(recipient.birthdate) },
    { id: "maritalStatus", label: "Situation familiale", value: maritalStatus },
    { id: "phone", label: "Téléphone", value: recipient.phone, copyValue: recipient.phone, mono: true },
    { id: "email", label: "Email", value: recipient.email, copyValue: recipient.email, truncate: true },
    { id: "address", label: "Adresse", value: formatAddress(recipient) },
    {
      id: "userManager",
      label: "Agent CAF en charge",
      value: recipient.userManagerDisplayed ? recipient.userManagerDisplayed.fullName : "",
    },
  ];
}

export function buildRecipientEyebrow(recipient, { constants = {} } = {}) {
  const origin = tools.findIn(constants.RECIPIENT_ORIGINS || [], "id", recipient.origin).name;
  const parts = ["Allocataire"];
  if (origin) parts.push(origin);
  if (recipient.createdAt) parts.push(`créé le ${formatDate(recipient.createdAt)}`);
  return parts.join(" · ");
}

// Pastilles d'état : compte, RDO, droits et devoirs, procédures en cours.
export function buildRecipientPills(recipient) {
  const pills = [];
  pills.push(
    recipient.isActivated
      ? { id: "activated", tone: "ok", icon: "fa-check", label: "Compte activé" }
      : { id: "activated", tone: "ko", icon: "fa-times", label: "Compte non activé" }
  );
  pills.push(
    recipient.rdspAvailable
      ? { id: "rdo", tone: "muted", icon: "fa-file-o", label: "Sans RDO" }
      : { id: "rdo", tone: "ok", icon: "fa-file-text-o", label: "RDO renseigné" }
  );
  const sdd = tools.getDroitsEtDevoirLibelle(recipient.rightsAndDuties || []);
  pills.push({ id: "sdd", tone: sdd === "SDD" ? "info" : "muted", label: sdd || "—" });
  if (recipient.currentOrientation) {
    pills.push({
      id: "reorientation",
      tone: "warn",
      icon: "fa-exchange",
      label: `Réorientation en cours depuis le ${formatDate(recipient.currentOrientation.createdAt)}`,
    });
  }
  if (recipient.currentEP && recipient.currentEP.EP) {
    pills.push({
      id: "ep",
      tone: "neutral",
      icon: "fa-gavel",
      label: `Inscrit à l'EP du ${formatDate(recipient.currentEP.EP.beginAt)}`,
    });
  }
  return pills;
}

export function getOrientationSummary(recipient, orgs = []) {
  const last = recipient.lastOrientation;
  if (!last || !last.orientation) return null;
  const org = tools.findIn(orgs, "slugName", last.orientation);
  return {
    name: org.name || last.partnerName || "N.D",
    date: formatDate(last.createdAt),
    type: getOrientationTypeLabel(last.type),
    maintainedAt: last.updatedAt ? formatDate(last.updatedAt) : "",
    orientationId: last.orientationId,
  };
}

// Onglets de la fiche : masqués par les données (pas encore chargées, pas
// d'allocataire) et par le rôle (même source que RoleStyle : roleConfig).
export function buildRecipientTabs({ recipient, rdsps, formConfig, events, rdvs, role }) {
  const forbidden = (role && roleConfig[role]) || [];
  const byRole = (dataPriv) => !!dataPriv && forbidden.includes(dataPriv);
  const isCaf = !recipient.lastOrientation || recipient.lastOrientation.orientation === "CAF";
  return [
    { id: "informations", label: "Informations", icon: "fa-id-card-o" },
    {
      id: "cer",
      label: "Contrat CER",
      icon: "fa-file-text-o",
      dataPriv: "crud_cer",
      hint: isCaf ? null : "non CAF",
      hidden: !recipient.id || byRole("crud_cer"),
    },
    {
      id: "rdo",
      label: "RDO",
      icon: "fa-list-alt",
      dataPriv: "view_rdsp",
      badge: Array.isArray(rdsps) ? rdsps.length : null,
      hidden: byRole("view_rdsp"),
    },
    {
      id: "rdv",
      label: "Rendez-vous",
      icon: "fa-calendar",
      dataPriv: "view_recipient_appointments",
      badge: Array.isArray(rdvs) ? rdvs.length : null,
      hidden: byRole("view_recipient_appointments"),
    },
    {
      id: "parcours",
      label: "Parcours",
      icon: "fa-road",
      badge: Array.isArray(events) ? events.length : null,
    },
  ];
}
